import { createFeatureSelector, createSelector } from '@ngrx/store';
import { ResponseStatus } from '@certemy/common';
import { WorkflowPhase } from 'app/pages/organization/testing-workflow/objects/workflow-phase.object';
import { WorkflowStep } from 'app/pages/organization/testing-workflow/objects/workflow-step.object';
import { Workflow } from 'app/pages/organization/testing-workflow/objects/workflow.object';
import {
  SystemProviderWorkflowFieldsConfig,
  WorflowConfigMap,
} from 'features/testing-workflow/testing-workflow-details/testing-workflow-detais.interface';
import { TestingProviderInfo, CurrentPhaseOptions, WORKFLOW_ACTION_TYPE } from '../workflow.interface';
import { PHASE_TYPES } from 'constants/status.constants';
import _values from 'lodash-es/values';
import _keyBy from 'lodash-es/keyBy';
import { PhasesInfoForStepDuplicationState } from 'features/testing-workflow/testing-workflow-details/duplicate-step/duplicate-step.objects';

export class WorkflowState {
  data: Workflow = null;
  initialWorkflowData: Workflow = null;
  selectedPhase: WorkflowPhase = null;
  selectedStep: WorkflowStep = null;
  status: ResponseStatus = ResponseStatus.INITIAL;
  config: WorflowConfigMap = null;
  testingProvider: TestingProviderInfo = null;
  detailsMode = false;
  currentPhaseNextOptions: CurrentPhaseOptions = null;
  systemProviderFields: SystemProviderWorkflowFieldsConfig = {};
  shouldPublishAfterImageUpload = false;
  defaultEducationActivitiesState = {
    status: ResponseStatus.INITIAL,
    activities: [],
  };
  action = WORKFLOW_ACTION_TYPE.INIT;
}
export namespace Selectors {
  export const workflowState = createFeatureSelector<WorkflowState>('workflow');

  export const getWorkflowStateStatus = createSelector(
    workflowState,
    state => state.status,
  );

  export const getWorkflow = createSelector(
    workflowState,
    state => state.data,
  );

  export const getInitialWorkflow = createSelector(
    workflowState,
    state => state.initialWorkflowData,
  );

  export const getSelectedPhase = createSelector(
    workflowState,
    state => state.selectedPhase,
  );

  export const getSelectedStep = createSelector(
    workflowState,
    state => state.selectedStep,
  );

  export const getDetailsMode = createSelector(
    workflowState,
    state => state.detailsMode,
  );

  export const getWorkflowConfig = createSelector(
    workflowState,
    state => state.config,
  );

  export const getTestingProvider = createSelector(
    workflowState,
    state => state.testingProvider,
  );

  export const getSystemProviderFields = createSelector(
    workflowState,
    state => state.systemProviderFields,
  );

  export const getCurrentPhaseOptions = createSelector(
    workflowState,
    state => state.currentPhaseNextOptions,
  );

  export const getMergedRegistrationAndScoreSpecificPhaseFields = createSelector(
    getWorkflow,
    (workflow: Workflow) => {
      if (!workflow) {
        return null;
      }
      const phaseTypes = [PHASE_TYPES.REGISTRATION, PHASE_TYPES.SCORE_REPORTING];
      const phases = workflow.phases.filter(phase => phaseTypes.includes(phase.typeId));

      return phases.reduce((specificPhaseFields, phase) => {
        return { ...specificPhaseFields, ...phase.specificPhaseField };
      }, {});
    },
  );

  export const getPhasesInfoForStepDuplication = createSelector(
    workflowState,
    (state: WorkflowState): PhasesInfoForStepDuplicationState => {
      return _keyBy(
        state.data.phases.map((phase: WorkflowPhase, index) => {
          const { phaseId, name, typeId, steps } = phase;
          const phaseConfig = state.config[typeId];
          const validStepTypes = _values(phaseConfig.stepConfiguration).map(config => {
            const { stepTypeId, required, requiredForCompletion } = config;
            return {
              stepTypeId,
              required,
              requiredForCompletion,
            };
          });
          return {
            phaseId,
            name,
            index,
            typeId,
            existingSteps: steps.map((step: WorkflowStep) => {
              return {
                id: step.id,
                name: step.name,
                typeId: step.typeId,
              };
            }),
            validStepTypes,
          };
        }),
        'phaseId',
      );
    },
  );

  export const getIsPublishAfterImageUpload = createSelector(
    workflowState,
    certification => certification.shouldPublishAfterImageUpload,
  );

  export const getEducationDefaultActivitiesState = createSelector(
    workflowState,
    workflow => workflow.defaultEducationActivitiesState,
  );

  export const getEducationDefaultActivities = createSelector(
    getEducationDefaultActivitiesState,
    actvitiesState => actvitiesState.activities,
  );

  export const getAction = createSelector(
    workflowState,
    workflow => workflow.action,
  );
}
